import { createSlice } from "@reduxjs/toolkit";

export const initialExternalOperationsState = {
  categories: {
    categoriesList: [],
    expense: {
      direct: {
        id: "direct",
        title: "Прямые",
        open: true,
        items: [],
      },
      operating: {
        id: "operating",
        title: "Операционные",
        open: true,
        items: [],
      },
      other: {
        id: "other",
        title: "Прочие",
        open: true,
        items: [],
      },
    },
    income: [],
  },
  operations: {
    filters: {
      type: { id: "type", title: "Тип операций", selected: true, value: "" },
      comment: { id: "comment", title: "Комментарии", selected: true, value: "" },
      date: { id: "date", title: "Дата", selected: true, value: new Date() },
      group: { id: "group", title: "Статья", selected: false, value: "" },
      regularity: { id: "regularity", title: "Регулярность", selected: false, value: false },
      marketplace: { id: "marketplace", title: "Маркетплейс", selected: false, value: "" },
      category: { id: "category", title: "Категория", selected: false, value: [] },
      brand: { id: "brand", title: "Бренд", selected: false, value: [] },
      supplier_article: {
        id: "supplier_article",
        title: "Артикул продавца",
        selected: false,
        value: [],
      },
      product_id_wb: { id: "product_id_wb", title: "Артикул WB", selected: false, value: [] },
      counterparty: { id: "counterparty", title: "Контрагент", selected: false, value: "" },
    },
    // ...
  },
};

const externalOperationsSlice = createSlice({
  name: "externalOperations",
  initialState: initialExternalOperationsState,
  reducers: {
    loadInitialCategoriesState(state, action) {
      state.categories = action.payload;
    },
    // верхнеуровневые категории (direct, operating, etc... существуют только для expense статей)
    // все остальные категории имеют более низкий порядок и названы здесь subcategory.
    // для income статей существуют только subcategories
    toggleExpenseCategory(state, action) {
      const { id } = action.payload;
      const cat = state.categories.expense[id];
      if (cat) {
        cat.open = !cat.open;
      }
    },
    toggleSubCategory(state, action) {
      const { type, mainId, subId } = action.payload;

      if (type === "expense") {
        const bucket = state.categories.expense[mainId];
        if (!bucket) return;

        const item = bucket.items.find(i => i.id === subId);
        if (item?.isCategory) {
          item.open = !item.open;
        }
      } else {
        const item = state.categories.income.find(i => i.id === subId);
        if (item?.isCategory) {
          item.open = !item.open;
        }
      }
    },
    deleteCategoryStore(state, action) {
      const { type, expenseType, categoryId } = action.payload;
      if (type === "expense") {
        if (!expenseType || !state.categories.expense[expenseType]) {
          console.error("deleteGroup: invalid expenseType", expenseType);
          return;
        }
        state.categories.expense[expenseType].items = state.categories.expense[
          expenseType
        ].items.filter(item => !(item.id === categoryId && item.isCategory));
      } else {
        state.categories.income = state.categories.income.filter(
          item => !(item.id === categoryId && item.isCategory)
        );
      }
    },
    deleteGroupStore(state, action) {
      const { type, expenseType, categoryId, groupId } = action.payload;
      if (type === "expense") {
        if (!expenseType || !state.categories.expense[expenseType]) {
          console.error("deleteGroup: invalid expenseType", expenseType);
          return;
        }
        // если ей айди, то значит, что статья в категории, иначе без категории
        if (categoryId) {
          const category = state.categories.expense[expenseType].items.find(
            item => item.id === categoryId && item.isCategory
          );
          if (!category) {
            console.error("deleteGroup: category not found");
            return;
          }
          category.items = category.items.filter(item => item.id !== groupId);
        } else {
          state.categories.expense[expenseType].items = state.categories.expense[
            expenseType
          ].items.filter(item => !(item.id === groupId && !item.isCategory));
        }
      } else {
        if (categoryId) {
          const category = state.categories.income.find(
            item => item.id === categoryId && item.isCategory
          );
          if (!category) {
            console.error("deleteGroup: category not found");
            return;
          }
          category.items = category.items.filter(item => item.id !== groupId);
        } else {
          state.categories.income = state.categories.income.filter(
            item => !(item.id === groupId && !item.isCategory)
          );
        }
      }
    },
    moveGroup(state, action) {
      const {
        type, // expense / income
        mainId, // direct / operating / other
        fromCategoryId,
        toCategoryId,
        groupId,
        newIndex,
      } = action.payload;
      const bucket =
        type === "expense" ? state.categories.expense[mainId] : { items: state.categories.income };
      if (!bucket) return;
      let movedGroup;
      if (fromCategoryId) {
        const fromCat = bucket.items.find(i => i.id === fromCategoryId);
        if (!fromCat) return;
        movedGroup = fromCat.items.find(i => i.id === groupId);
        fromCat.items = fromCat.items.filter(i => i.id !== groupId);
      } else {
        movedGroup = bucket.items.find(i => i.id === groupId && !i.isCategory);
        bucket.items = bucket.items.filter(i => i.id !== groupId);
      }
      if (!movedGroup) return;
      if (toCategoryId) {
        const toCat = bucket.items.find(i => i.id === toCategoryId);
        if (!toCat) return;
        toCat.items.splice(newIndex, 0, movedGroup);
      } else {
        bucket.items.splice(newIndex, 0, movedGroup);
      }
    },
    toggleOperationsFilter(state, action) {
      const { filterType } = action.payload;
      if (!filterType) {
        console.error("toggleOperationsFilter: invalid filterType");
        return;
      }
      state.operations.filters[filterType].selected =
        !state.operations.filters[filterType].selected;
    },
    setOperationsFilterValue(state, action) {
      const { filterType, value } = action.payload;
      if (!filterType || !(filterType in state.operations.filters)) {
        console.error("setOperationsFilterValue: invalid filterType", filterType);
        return;
      }
      state.operations.filters[filterType].value = value;
    },
  },
});

export const {
  loadInitialCategoriesState,
  toggleExpenseCategory,
  toggleSubCategory,
  deleteCategoryStore,
  deleteGroupStore,
  toggleOperationsFilter,
  setOperationsFilterValue,
  moveGroup,
} = externalOperationsSlice.actions;

export default externalOperationsSlice.reducer;
